import { getLogger } from '../utils/logger/logger';

// Short at first, so a check that ran a moment too early is retried before the user has started using the page
const RETRY_DELAYS_MS = [1, 2, 4, 8, 15].map(seconds => seconds * 1000);
// A half-up network can leave a request hanging for minutes, which would block every retry
const ATTEMPT_TIMEOUT_MS = 5 * 1000;

const logger = getLogger('[auto-reload]');

/**
 * @description Reloads once the current page can be fetched. Right after waking, the network is often not back yet, and
 * reloading then would swap a working app for the browser's offline error page.
 * @param reload Called once, with the number of checks that failed before the page was reachable.
 * @returns Cleanup that cancels any pending attempt.
 */
export const reloadWhenReachable = (reload: (failedChecks: number) => void = () => window.location.reload()) => {
  let cancelled = false;
  let request: AbortController | undefined;
  let retryTimeout: ReturnType<typeof setTimeout> | undefined;
  let attempting = false;
  let retries = 0;
  let failedChecks = 0;

  const attempt = async () => {
    if (attempting) {
      return;
    }
    attempting = true;
    clearTimeout(retryTimeout);
    const currentRequest = new AbortController();
    request = currentRequest;
    const deadline = setTimeout(() => currentRequest.abort(), ATTEMPT_TIMEOUT_MS);
    const reachable = await fetch(window.location.href, { cache: 'no-store', signal: currentRequest.signal }).then(
      response => response.ok,
      () => false
    );
    clearTimeout(deadline);
    attempting = false;

    if (cancelled) {
      return;
    }
    if (reachable) {
      // The caller may choose not to reload, and a later online event must not ask again
      window.removeEventListener('online', onOnline);
      reload(failedChecks);
      return;
    }
    const delay = RETRY_DELAYS_MS[Math.min(retries, RETRY_DELAYS_MS.length - 1)];
    retries++;
    failedChecks++;
    logger.info(`app unreachable, retrying reload in ${delay / 1000}s`);
    retryTimeout = setTimeout(attempt, delay);
  };

  // The first check after reconnecting often runs before DNS is back, so restart the short delays
  const onOnline = () => {
    retries = 0;
    void attempt();
  };

  window.addEventListener('online', onOnline);
  void attempt();

  return () => {
    cancelled = true;
    request?.abort();
    clearTimeout(retryTimeout);
    window.removeEventListener('online', onOnline);
  };
};
